import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { CheckCircle, XCircle, Clock, ArrowRight, Mail } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

export default function BetalingPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const orderId = params.get("orderId");

  const [status, setStatus] = useState("laden"); // laden | betaald | open | mislukt | niet_gevonden
  const [order, setOrder] = useState(null);
  const [pogingen, setPogingen] = useState(0);

  useEffect(() => {
    if (!orderId) { setStatus("niet_gevonden"); return; }

    const check = async () => {
      try {
        const res = await fetch(`${API}/api/payments/status/${orderId}`);
        const data = await res.json();
        setOrder(data);
        if (data.betaald) {
          setStatus("betaald");
        } else if (["mislukt", "geannuleerd", "verlopen"].includes(data.status)) {
          setStatus("mislukt");
        } else {
          // Nog in behandeling — opnieuw proberen (max 8x)
          setPogingen(p => p + 1);
          if (pogingen < 8) {
            setTimeout(check, 2500);
          } else {
            setStatus("open");
          }
        }
      } catch {
        setStatus("niet_gevonden");
      }
    };
    check();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderId]);

  return (
    <div className="min-h-screen bg-[#080808] flex items-center justify-center px-4">
      <div className="max-w-md w-full text-center">

        {/* Logo */}
        <p className="text-[#C9A84C] text-xs font-bold uppercase tracking-[4px] mb-12">FitSmith</p>

        {/* Laden */}
        {status === "laden" && (
          <div>
            <div className="w-14 h-14 border-2 border-[#C9A84C] border-t-transparent rounded-full animate-spin mx-auto mb-6" />
            <h1 className="text-white font-bold text-2xl mb-2">Betaling controleren...</h1>
            <p className="text-zinc-500 text-sm">Even geduld, we verwerken je betaling.</p>
          </div>
        )}

        {/* Succes */}
        {status === "betaald" && (
          <div>
            <div className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6"
              style={{ background: "rgba(201,168,76,0.1)", border: "2px solid rgba(201,168,76,0.3)" }}>
              <CheckCircle size={40} className="text-[#C9A84C]" />
            </div>
            <h1 className="text-white font-bold text-3xl mb-3">Betaling geslaagd!</h1>
            <p className="text-zinc-400 text-base mb-2">
              Bedankt voor je bestelling{order?.naam ? `, ${order.naam.split(" ")[0]}` : ""}!
            </p>
            <p className="text-zinc-500 text-sm mb-8 leading-relaxed">
              Je ontvangt een bevestiging op{" "}
              <span className="text-white font-semibold">{order?.email}</span>.
            </p>

            {/* Persoonlijk schema flow */}
            {order?.product === "persoonlijk_schema" && (
              <div className="space-y-3">
                <div className="flex items-center gap-3 bg-[#111] border border-white/6 rounded-xl px-4 py-3 text-left">
                  <Mail size={16} className="text-[#C9A84C] shrink-0" />
                  <div>
                    <p className="text-white text-sm font-semibold">Check je inbox</p>
                    <p className="text-zinc-500 text-xs">Intake-uitnodiging onderweg naar {order?.email}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3 bg-[#111] border border-white/6 rounded-xl px-4 py-3 text-left">
                  <div className="w-4 h-4 rounded-full flex items-center justify-center shrink-0"
                    style={{ background: "rgba(201,168,76,0.15)" }}>
                    <span className="text-[#C9A84C] text-[10px] font-black">2</span>
                  </div>
                  <div>
                    <p className="text-white text-sm font-semibold">Vul het intakeformulier in</p>
                    <p className="text-zinc-500 text-xs">Zo kunnen we het schema perfect afstemmen</p>
                  </div>
                </div>
                <div className="flex items-center gap-3 bg-[#111] border border-white/6 rounded-xl px-4 py-3 text-left">
                  <div className="w-4 h-4 rounded-full flex items-center justify-center shrink-0"
                    style={{ background: "rgba(201,168,76,0.15)" }}>
                    <span className="text-[#C9A84C] text-[10px] font-black">3</span>
                  </div>
                  <div>
                    <p className="text-white text-sm font-semibold">Ontvang je persoonlijk schema</p>
                    <p className="text-zinc-500 text-xs">Binnen 48 uur klaarstaan in jouw dashboard</p>
                  </div>
                </div>
              </div>
            )}

            {/* Cursus flow */}
            {order?.product === "cursus" && (
              <div className="space-y-3">
                <div className="flex items-center gap-3 bg-[#111] border border-white/6 rounded-xl px-4 py-3 text-left">
                  <Mail size={16} className="text-[#C9A84C] shrink-0" />
                  <div>
                    <p className="text-white text-sm font-semibold">Check je inbox</p>
                    <p className="text-zinc-500 text-xs">Bevestiging onderweg naar {order?.email}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3 bg-[#111] border border-white/6 rounded-xl px-4 py-3 text-left">
                  <div className="w-4 h-4 rounded-full flex items-center justify-center shrink-0"
                    style={{ background: "rgba(201,168,76,0.15)" }}>
                    <span className="text-[#C9A84C] text-[10px] font-black">2</span>
                  </div>
                  <div>
                    <p className="text-white text-sm font-semibold">Account aanmaken</p>
                    <p className="text-zinc-500 text-xs">Je ontvangt je inloggegevens per e-mail</p>
                  </div>
                </div>
                <div className="flex items-center gap-3 bg-[#111] border border-white/6 rounded-xl px-4 py-3 text-left">
                  <div className="w-4 h-4 rounded-full flex items-center justify-center shrink-0"
                    style={{ background: "rgba(201,168,76,0.15)" }}>
                    <span className="text-[#C9A84C] text-[10px] font-black">3</span>
                  </div>
                  <div>
                    <p className="text-white text-sm font-semibold">Start de cursus</p>
                    <p className="text-zinc-500 text-xs">20 lessen + alle schema's direct beschikbaar</p>
                  </div>
                </div>
              </div>
            )}

            {/* Standaard schema flow */}
            {order?.product === "standaard_schema" && (
              <div className="space-y-3">
                <div className="flex items-center gap-3 bg-[#111] border border-white/6 rounded-xl px-4 py-3 text-left">
                  <Mail size={16} className="text-[#C9A84C] shrink-0" />
                  <div>
                    <p className="text-white text-sm font-semibold">Check je inbox</p>
                    <p className="text-zinc-500 text-xs">Bevestiging onderweg naar {order?.email}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3 bg-[#111] border border-white/6 rounded-xl px-4 py-3 text-left">
                  <div className="w-4 h-4 rounded-full flex items-center justify-center shrink-0"
                    style={{ background: "rgba(201,168,76,0.15)" }}>
                    <span className="text-[#C9A84C] text-[10px] font-black">2</span>
                  </div>
                  <div>
                    <p className="text-white text-sm font-semibold">Schema klaarstaan</p>
                    <p className="text-zinc-500 text-xs">Je ontvangt inloggegevens zodra je account gereed is</p>
                  </div>
                </div>
                <div className="flex items-center gap-3 bg-[#111] border border-white/6 rounded-xl px-4 py-3 text-left">
                  <div className="w-4 h-4 rounded-full flex items-center justify-center shrink-0"
                    style={{ background: "rgba(201,168,76,0.15)" }}>
                    <span className="text-[#C9A84C] text-[10px] font-black">3</span>
                  </div>
                  <div>
                    <p className="text-white text-sm font-semibold">Direct starten</p>
                    <p className="text-zinc-500 text-xs">Schema zichtbaar in jouw persoonlijk dashboard</p>
                  </div>
                </div>
              </div>
            )}

            <button onClick={() => navigate("/")}
              data-testid="betaling-terug-home"
              className="mt-8 w-full py-3 rounded-xl border border-white/10 text-zinc-400 hover:text-white text-sm font-semibold transition-colors flex items-center justify-center gap-2">
              Terug naar FitSmith <ArrowRight size={14} />
            </button>
          </div>
        )}

        {/* Nog in behandeling */}
        {status === "open" && (
          <div>
            <div className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6"
              style={{ background: "rgba(251,191,36,0.1)", border: "2px solid rgba(251,191,36,0.3)" }}>
              <Clock size={40} className="text-amber-400" />
            </div>
            <h1 className="text-white font-bold text-2xl mb-3">Betaling in behandeling</h1>
            <p className="text-zinc-400 text-sm mb-6 leading-relaxed">
              Je betaling wordt nog verwerkt. Dit kan een paar minuten duren.
              Je ontvangt een bevestiging per e-mail zodra alles rond is.
            </p>
            <p className="text-zinc-600 text-xs mb-6">Ordernummer: {orderId}</p>
            <button onClick={() => navigate("/")}
              className="mt-2 w-full py-3 rounded-xl border border-white/10 text-zinc-400 text-sm font-semibold transition-colors">
              Terug naar home
            </button>
          </div>
        )}

        {/* Mislukt */}
        {status === "mislukt" && (
          <div>
            <div className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6"
              style={{ background: "rgba(239,68,68,0.1)", border: "2px solid rgba(239,68,68,0.3)" }}>
              <XCircle size={40} className="text-red-400" />
            </div>
            <h1 className="text-white font-bold text-2xl mb-3">Betaling niet geslaagd</h1>
            <p className="text-zinc-400 text-sm mb-8 leading-relaxed">
              De betaling is mislukt, geannuleerd of verlopen. Je bent niets in rekening gebracht.
            </p>
            <button onClick={() => navigate("/bestellen?product=" + (order?.product || "persoonlijk_schema"))}
              data-testid="betaling-opnieuw"
              className="w-full py-3 rounded-xl font-bold text-sm uppercase tracking-wider mb-3"
              style={{ background: "#C9A84C", color: "#000" }}>
              Opnieuw proberen
            </button>
            <button onClick={() => navigate("/")}
              className="w-full py-3 rounded-xl border border-white/10 text-zinc-400 text-sm font-semibold transition-colors">
              Terug naar home
            </button>
          </div>
        )}

        {/* Niet gevonden */}
        {status === "niet_gevonden" && (
          <div>
            <h1 className="text-white font-bold text-2xl mb-3">Order niet gevonden</h1>
            <p className="text-zinc-500 text-sm mb-8">We konden je bestelling niet terugvinden.</p>
            <button onClick={() => navigate("/")}
              className="w-full py-3 rounded-xl border border-white/10 text-zinc-400 text-sm font-semibold">
              Terug naar home
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
